import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, Smartphone, FileText, Stethoscope, Tag, X, Info } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * Universal checkout page. Reads a `draft` query-param, fetches the draft,
 * lets the customer pick Stripe or Vipps, and only on payment success is
 * an order ever created server-side. For the diagnostic-approval draft
 * variant we also render the fixer's findings under the summary.
 *
 * The promo-code flow re-creates the draft when a code is applied/cleared
 * so the server is always the source of truth for the discounted total —
 * the frontend never trusts client-computed prices.
 */
export default function Checkout() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const initialDraftId = params.get("draft");
  const [draftId, setDraftId] = useState(initialDraftId);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState(null);
  const [method, setMethod] = useState("stripe");
  const [busy, setBusy] = useState(false);

  // Promo state — applied = currently in effect on the draft.
  const [promoInput, setPromoInput] = useState("");
  const [promoError, setPromoError] = useState("");
  const [promoBusy, setPromoBusy] = useState(false);

  useEffect(() => {
    if (!draftId) {
      setError("Mangler kassen-utkast. Start bestillingen på nytt.");
      return;
    }
    api
      .get(`/checkout/draft/${draftId}`)
      .then((r) => setDraft(r.data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [draftId]);

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        draft_id: draftId,
        method,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setBusy(false);
    }
  };

  /**
   * Rebuild the underlying draft with a new promo code (or none).
   * The original draft is "abandoned" — we ignore the old id. The flow stays
   * inside `/kassen` so the URL also updates with the new draft id.
   */
  const rebuildDraft = async (code) => {
    if (!draft) return;
    setPromoBusy(true);
    try {
      const p = draft.payload;
      let res;
      if (draft.type === "repair") {
        res = await api.post("/checkout/draft/repair", {
          product_id: p.product_id,
          service_id: p.service_id,
          fixer_id: p.fixer_id,
          delivery_method: p.delivery_method,
          include_diagnostic: !!p.include_diagnostic,
          promo_code: code || null,
        });
      } else if (draft.type === "diagnostic") {
        res = await api.post("/checkout/draft/diagnostic", {
          device_category: p.device_category,
          device_model: p.device_model || null,
          symptoms: p.symptoms || [],
          description: p.description || null,
          fixer_id: p.fixer_id,
          delivery_method: p.delivery_method,
          promo_code: code || null,
        });
      } else {
        // Approval drafts don't have a public re-create endpoint — skip.
        toast.error("Rabattkode kan ikke brukes for denne typen bestilling");
        return;
      }
      setDraft(res.data);
      setDraftId(res.data.id);
      nav(`/kassen?draft=${res.data.id}`, { replace: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setPromoBusy(false);
    }
  };

  const applyPromo = async (e) => {
    e?.preventDefault?.();
    const code = promoInput.trim();
    if (!code) return;
    setPromoError("");
    setPromoBusy(true);
    try {
      // Validate first so we can surface the precise error inline.
      await api.post("/promo-codes/validate", { code });
      await rebuildDraft(code);
      toast.success("Rabattkode brukt");
    } catch (err) {
      setPromoError(formatApiErrorDetail(err.response?.data?.detail) || "Ugyldig kode — prøv igjen");
    } finally {
      setPromoBusy(false);
    }
  };

  const clearPromo = async () => {
    setPromoInput("");
    setPromoError("");
    await rebuildDraft(null);
  };

  if (error) {
    return (
      <div className="tk-container py-20 max-w-xl text-center" data-testid="checkout-error">
        <h1 className="text-2xl font-semibold mb-2">Kunne ikke åpne kassen</h1>
        <p className="text-slate-500 mb-2">{error}</p>
        <p className="text-xs text-slate-400 mb-8">
          Kontakt oss på{" "}
          <a className="underline" href="mailto:post@tekkdeler.no">post@tekkdeler.no</a> hvis du trenger hjelp.
        </p>
        <button onClick={() => nav("/butikk")} className="tk-btn-primary" data-testid="back-to-shop-btn">
          Tilbake til butikken
        </button>
      </div>
    );
  }

  if (!draft) return <div className="tk-container py-20 text-slate-500" data-testid="checkout-loading">Laster …</div>;

  const p = draft.payload;
  const isApproval = draft.type === "diagnostic_approval";
  const isBring = p.delivery_method === "shipping";
  const hasPromo = Boolean(p.promo_code);

  return (
    <div className="tk-container py-16 grid grid-cols-1 lg:grid-cols-3 gap-12" data-testid="checkout-page">
      {/* LEFT — payment method selection */}
      <div className="lg:col-span-2">
        <p className="tk-overline mb-3">Kassen</p>
        <h1 className="text-3xl sm:text-4xl font-semibold mb-2">Velg betalingsmåte</h1>
        <p className="text-sm text-slate-500 mb-10">
          Alle beløp i NOK. Sikker betaling via Stripe eller Vipps.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            type="button"
            onClick={() => setMethod("stripe")}
            className={`text-left p-6 rounded-md border ${method === "stripe" ? "border-[#0B1C30] bg-slate-50" : "border-[#E2E8F0] bg-white"}`}
            data-testid="pay-method-stripe"
          >
            <CreditCard className="mb-3" />
            <p className="font-semibold">Kort (Stripe)</p>
            <p className="text-xs text-slate-500 mt-1">Visa, Mastercard, Apple Pay</p>
          </button>
          <button
            type="button"
            onClick={() => setMethod("vipps")}
            className={`text-left p-6 rounded-md border ${method === "vipps" ? "border-[#0B1C30] bg-slate-50" : "border-[#E2E8F0] bg-white"}`}
            data-testid="pay-method-vipps"
          >
            <Smartphone className="mb-3 text-orange-500" />
            <p className="font-semibold">
              Vipps <span className="text-xs text-slate-400 font-normal">(demo)</span>
            </p>
            <p className="text-xs text-slate-500 mt-1">Norges mest brukte mobilbetaling</p>
          </button>
        </div>

        {isBring && (
          <p
            className="mt-4 text-xs text-slate-500 flex items-start gap-2 max-w-md"
            data-testid="bring-info-note"
          >
            <Info size={14} className="mt-0.5 shrink-0" />
            Du mottar en Bring-fraktetikett på e-post etter bestilling. Etiketten finner
            du også under «Mine bestillinger».
          </p>
        )}

        {/* Promo code field */}
        {!isApproval && (
          <form onSubmit={applyPromo} className="mt-8 max-w-md" data-testid="promo-section">
            <label className="text-sm font-medium text-slate-700 flex items-center gap-2 mb-2">
              <Tag size={14} /> Rabattkode
            </label>
            {hasPromo ? (
              <div
                className="flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-emerald-200 bg-emerald-50"
                data-testid="promo-applied"
              >
                <div className="text-sm">
                  <span className="font-mono font-semibold">{p.promo_code}</span>
                  <span className="text-emerald-700 ml-2">−{p.discount_percent}%</span>
                </div>
                <button
                  type="button"
                  onClick={clearPromo}
                  disabled={promoBusy}
                  className="text-slate-500 hover:text-slate-800"
                  data-testid="promo-clear-btn"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <>
                <div className="flex gap-2">
                  <input
                    className="tk-input"
                    placeholder="Skriv inn kode"
                    value={promoInput}
                    onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                    data-testid="promo-input"
                  />
                  <button
                    type="submit"
                    disabled={promoBusy || !promoInput.trim()}
                    className="tk-btn-secondary"
                    data-testid="promo-apply-btn"
                  >
                    {promoBusy ? "…" : "Bruk kode"}
                  </button>
                </div>
                {promoError && (
                  <p className="text-xs text-red-600 mt-1" data-testid="promo-error">
                    {promoError}
                  </p>
                )}
              </>
            )}
          </form>
        )}

        <button
          onClick={pay}
          disabled={busy}
          className="tk-btn-primary mt-10"
          data-testid="pay-now-btn"
        >
          {busy ? "Sender deg videre …" : `Betal kr ${Number(p.total_price).toFixed(0)}`}
        </button>

        <p className="text-xs text-slate-400 mt-3">
          Ingen ordre opprettes før betalingen er fullført.
        </p>

        {/* Diagnostic findings for approval flow */}
        {isApproval && (
          <div className="tk-card mt-10" data-testid="approval-findings">
            <p className="tk-overline mb-3 flex items-center gap-2">
              <Stethoscope size={14} /> Verkstedets diagnose
            </p>
            <p className="text-sm mb-2">
              <span className="text-slate-500">Funn:</span> {p.diagnostic_findings}
            </p>
            <p className="text-sm mb-2">
              <span className="text-slate-500">Anbefalt reparasjon:</span> {p.recommended_repair}
            </p>
            <p className="text-sm">
              <span className="text-slate-500">Tilleggspris:</span>{" "}
              <span className="font-semibold">kr {Number(p.recommended_price).toFixed(0)}</span>
            </p>
          </div>
        )}
      </div>

      {/* RIGHT — order summary */}
      <div className="tk-card h-fit" data-testid="checkout-summary">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <FileText size={16} /> Sammendrag
        </h3>
        {isApproval ? (
          <div className="space-y-2 text-sm">
            <Row label="Bestilling" value={`#${p.order_id.slice(0, 8)}`} />
            <Row label="Enhet" value={p.product_name} />
            <Row label="Verksted" value={p.fixer_display_name} />
            <Row label="Reparasjon" value={p.recommended_repair} />
          </div>
        ) : draft.type === "diagnostic" ? (
          <div className="space-y-2 text-sm">
            <Row label="Tjeneste" value="Diagnose" />
            <Row label="Enhet" value={p.device_category} />
            {p.device_model && <Row label="Modell" value={p.device_model} />}
            {p.symptoms?.length > 0 && (
              <Row label="Symptomer" value={p.symptoms.join(", ")} />
            )}
            <Row label="Verksted" value={p.fixer_display_name} />
            <Row label="Levering" value={p.delivery_method === "shipping" ? "Bring-frakt" : "Selv-levering"} />
          </div>
        ) : (
          <div className="space-y-2 text-sm">
            <Row label="Produkt" value={p.product_name} />
            <Row label="Reparasjon" value={p.service_name} />
            {p.include_diagnostic && <Row label="Inkl." value="Diagnose først" />}
            <Row label="Verksted" value={p.fixer_display_name} />
            <Row label="Levering" value={p.delivery_method === "shipping" ? "Bring-frakt" : "Selv-levering"} />
          </div>
        )}
        <div className="tk-hairline my-4" />
        {!isApproval && (
          <div className="space-y-1 text-sm">
            {draft.type === "repair" && (
              <Row label="Del" value={`kr ${Number(p.part_price).toFixed(0)}`} />
            )}
            <Row
              label={draft.type === "diagnostic" ? "Diagnose-avgift" : "Arbeid"}
              value={`kr ${Number(p.service_fee).toFixed(0)}`}
            />
            {p.diagnose_fee > 0 && (
              <Row label="Diagnose" value={`kr ${Number(p.diagnose_fee).toFixed(0)}`} />
            )}
            {p.discount_amount > 0 && (
              <Row
                label={`Rabatt (${p.discount_percent}%)`}
                value={`− kr ${Number(p.discount_amount).toFixed(0)}`}
              />
            )}
          </div>
        )}
        {isApproval && (
          <Row label="Reparasjon" value={`kr ${Number(p.recommended_price).toFixed(0)}`} />
        )}
        <div className="tk-hairline my-4" />
        <div className="flex justify-between text-base">
          <span className="font-medium">Total</span>
          <span className="font-semibold" data-testid="checkout-total">
            kr {Number(p.total_price).toFixed(0)}
          </span>
        </div>

        <Link to="/butikk" className="text-xs text-slate-400 underline mt-4 inline-block">
          Avbryt og fortsett å handle
        </Link>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-800 text-right">{value}</span>
    </div>
  );
}
